import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  ListChecks, Plus, X, Save, Sparkles, RefreshCw, ArrowLeft,
} from "lucide-react";

const BUCKETS = [
  { key: "thirty_day", label: "Next 30 days" },
  { key: "sixty_day", label: "Days 31–60" },
  { key: "ninety_day", label: "Days 61–90" },
];

function newItem(text = "") {
  return { id: Math.random().toString(36).slice(2), text, done: false };
}

function ExploreBlock({ testid, label, items }) {
  return (
    <div className="mt-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-accent-warm">{label}</p>
      <ul className="mt-1 space-y-2">
        {(items || []).length === 0 && (
          <li className="text-xs text-fg-muted italic" data-testid={`${testid}-empty`}>
            No items in this block yet.
          </li>
        )}
        {(items || []).map((it, i) => (
          <li
            key={it.id || i}
            className="text-sm text-fg-primary"
            data-testid={`${testid}-item-${i}`}
            data-source={it.source}
            data-personalized={String(!!it.personalized)}
          >
            <span className="mr-2 text-progress-teal" aria-hidden="true">•</span>
            {it.text}
            {it.label && (
              <span
                className="ml-2 inline-block rounded border border-fg-muted/40 bg-bg-base px-1.5 py-0.5 text-[10px] italic text-fg-muted"
                data-testid={`${testid}-item-${i}-general-label`}
              >
                {it.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ActionPlan() {
  const [state, setState] = useState({ thirty_day: [], sixty_day: [], ninety_day: [] });
  const [scaffold, setScaffold] = useState(null);
  const [exploration, setExploration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftPanelOpen, setDraftPanelOpen] = useState(false);
  const [draftFocus, setDraftFocus] = useState("");
  const [draftResult, setDraftResult] = useState(null);
  const [draftError, setDraftError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/action-plan");
      setScaffold(data.scaffold);
      setExploration(data.exploration || null);
      if (data.plan) {
        setState({
          thirty_day: data.plan.thirty_day || [],
          sixty_day: data.plan.sixty_day || [],
          ninety_day: data.plan.ninety_day || [],
        });
      } else if (data.scaffold) {
        // Preload scaffold — participant edits and saves to commit
        setState({
          thirty_day: (data.scaffold.thirty_day || []).map((x) => ({ ...x })),
          sixty_day: (data.scaffold.sixty_day || []).map((x) => ({ ...x })),
          ninety_day: (data.scaffold.ninety_day || []).map((x) => ({ ...x })),
        });
      }
    } catch (err) {
      setError(extractApiError(err, "Failed to load plan."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (bucket, idx, patch) => {
    setState((s) => ({ ...s, [bucket]: s[bucket].map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };
  const remove = (bucket, idx) => {
    setState((s) => ({ ...s, [bucket]: s[bucket].filter((_, i) => i !== idx) }));
  };
  const add = (bucket) => {
    setState((s) => ({ ...s, [bucket]: [...s[bucket], newItem("")] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        thirty_day: state.thirty_day.filter((i) => i.text.trim()).map((i) => ({
          id: i.id, text: i.text.trim(), done: !!i.done,
        })),
        sixty_day: state.sixty_day.filter((i) => i.text.trim()).map((i) => ({
          id: i.id, text: i.text.trim(), done: !!i.done,
        })),
        ninety_day: state.ninety_day.filter((i) => i.text.trim()).map((i) => ({
          id: i.id, text: i.text.trim(), done: !!i.done,
        })),
      };
      await api.put("/action-plan", payload);
      toast.success("Plan saved. Prior state preserved as a revision.");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const requestDraft = async () => {
    setDraftBusy(true);
    setDraftError(null);
    setDraftResult(null);
    try {
      const { data } = await api.post("/action-plan/draft", {
        focus: draftFocus.trim() || null,
      });
      setDraftResult(data);
    } catch (err) {
      setDraftError(extractApiError(err, "Draft unavailable right now."));
    } finally {
      setDraftBusy(false);
    }
  };

  const acceptDraft = (bucket) => {
    if (!draftResult) return;
    setState((s) => ({
      ...s,
      [bucket]: [...s[bucket], ...draftResult[bucket].map((x) => ({ ...x }))],
    }));
    toast.success(`Added ${draftResult[bucket].length} draft items — not saved until you click Save.`);
  };

  if (loading) {
    return <AppShell><div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="action-plan-loading">Loading plan…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">Plan</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary flex items-center gap-2" data-testid="action-plan-title">
              <ListChecks className="h-6 w-6" aria-hidden="true" />
              Your 30/60/90-day plan
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-fg-secondary">
              This is your personal action plan. Edit the suggested
              tasks, then select <strong>Save plan</strong>.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="action-plan-save-btn" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
              <Save className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              {saving ? "Saving…" : "Save plan"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraftPanelOpen((v) => !v)} data-testid="action-plan-ai-toggle" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
              <Sparkles className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              {draftPanelOpen ? "Hide AI draft" : "Get an AI draft (optional)"}
            </Button>
          </div>
        </div>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        {/* 30-60 day EXPLORATION plan blocks (Mark 2026-09-23c
            Three-Directions clarity task). Rendered above the older
            30/60/90-day plan editor so the participant sees a clear
            three-step exploration shape for the selected direction
            first, then can edit the deeper plan if they want.  */}
        {exploration && (
          <section
            data-testid="exploration-plan"
            className="mb-8 rounded-lg border border-progress-teal/30 bg-progress-teal/5 p-5"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-progress-teal">
                  30–60 day exploration
                </p>
                <h2
                  className="mt-1 text-xl font-heading font-medium text-fg-primary"
                  data-testid="exploration-plan-heading"
                >
                  {exploration.direction_title
                    ? <>Exploring: <span className="text-progress-teal">{exploration.direction_title}</span></>
                    : "Exploring your selected direction"}
                </h2>
                <p className="mt-1 text-xs text-fg-secondary">
                  {exploration.confirmed
                    ? exploration.flexible_time_note
                    : "You haven't selected a direction yet. Pick one from the Results workspace to unlock a personalized exploration plan."}
                </p>
              </div>
              {!exploration.confirmed && (
                <Link to="/results">
                  <Button
                    size="sm" variant="outline"
                    data-testid="exploration-plan-go-results-btn"
                    className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                  >
                    Open results
                  </Button>
                </Link>
              )}
            </div>

            {exploration.confirmed && (
              <>
                {/* Start here — ONE small action. */}
                <div className="mt-4" data-testid="exploration-start-here">
                  <p className="text-[11px] uppercase tracking-wider text-accent-warm">Start here</p>
                  <p className="mt-1 text-sm text-fg-primary">
                    {exploration.start_here.text}
                  </p>
                  {exploration.start_here.label && (
                    <p
                      className="mt-1 text-[11px] italic text-fg-muted"
                      data-testid="exploration-start-here-general-label"
                    >
                      {exploration.start_here.label}
                    </p>
                  )}
                </div>

                {/* First 30 days */}
                <ExploreBlock
                  testid="exploration-first-30"
                  label="First 30 days"
                  items={exploration.first_30_days || []}
                />

                {/* Days 31-60 */}
                <ExploreBlock
                  testid="exploration-days-31-60"
                  label="Days 31–60"
                  items={exploration.days_31_60 || []}
                />

                <p
                  className="mt-4 text-xs italic text-fg-muted"
                  data-testid="exploration-reassess-prompt"
                >
                  {exploration.reassess_prompt}
                </p>
              </>
            )}
          </section>
        )}

        {draftPanelOpen && (
          <div className="mb-6 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-4" data-testid="ai-draft-panel">
            <p className="text-[11px] uppercase tracking-wider text-accent-warm">DRAFT — not saved</p>
            <p className="mt-1 text-xs text-fg-secondary">
              Requires AI-processing consent. The draft is a suggestion — nothing is
              added to your plan unless you explicitly click "Add to plan".
            </p>
            <div className="mt-2">
              <label className="text-xs text-fg-primary">Focus (optional)</label>
              <Textarea
                data-testid="ai-draft-focus-input"
                className="mt-1 bg-bg-base border-border-subtle text-fg-primary text-sm"
                placeholder="e.g., I want to keep evenings for caregiving"
                maxLength={200}
                value={draftFocus}
                onChange={(e) => setDraftFocus(e.target.value)}
              />
            </div>
            <Button size="sm" onClick={requestDraft} disabled={draftBusy} data-testid="ai-draft-run-btn" className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
              {draftBusy ? "Drafting…" : "Request draft"}
            </Button>
            {draftError && <Alert variant="destructive" className="mt-3"><AlertDescription data-testid="ai-draft-error">{draftError}</AlertDescription></Alert>}
            {draftResult && (
              <div className="mt-4 space-y-3" data-testid="ai-draft-result">
                <p className="text-xs italic text-fg-secondary">{draftResult.draft_label}</p>
                {BUCKETS.map((b) => (
                  <div key={b.key} className="rounded-md border border-border-subtle bg-bg-base p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-fg-primary font-medium">{b.label}</p>
                      <Button size="sm" variant="ghost" onClick={() => acceptDraft(b.key)} data-testid={`ai-draft-accept-${b.key}`} className="text-accent-warm hover:bg-accent-warm/10">
                        Add to plan
                      </Button>
                    </div>
                    <ul className="mt-2 list-disc pl-5 text-sm text-fg-primary space-y-1">
                      {(draftResult[b.key] || []).map((x) => <li key={x.id}>{x.text}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {BUCKETS.map((b) => (
          <section key={b.key} className="mt-6" data-testid={`bucket-${b.key}`}>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-medium text-fg-primary">{b.label}</h2>
              <Button size="sm" variant="ghost" onClick={() => add(b.key)} data-testid={`bucket-${b.key}-add-btn`} className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                <Plus className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Add item
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {(state[b.key] || []).length === 0 && (
                <div
                  data-testid={`bucket-${b.key}-empty`}
                  className="rounded-md border border-dashed border-border-subtle bg-bg-surface p-4"
                >
                  <p className="text-sm text-fg-secondary">
                    You haven&rsquo;t added action-plan tasks for{" "}
                    {b.label} yet.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => add(b.key)}
                      data-testid={`bucket-${b.key}-empty-add-btn`}
                      className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                    >
                      <Plus className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                      Add item
                    </Button>
                    <Link
                      to="/training"
                      data-testid={`bucket-${b.key}-empty-training-link`}
                      className="text-xs underline text-fg-secondary hover:text-fg-primary"
                    >
                      View AI Training
                    </Link>
                  </div>
                </div>
              )}
              {(state[b.key] || []).map((it, i) => (
                <div key={it.id || i} className="rounded-md border border-border-subtle bg-bg-surface p-3 flex gap-2 items-start" data-testid={`bucket-${b.key}-item-${i}`}>
                  <input
                    type="checkbox"
                    className="mt-2 accent-progress-teal"
                    aria-label={`Mark done — ${b.label} item ${i + 1}`}
                    checked={!!it.done}
                    onChange={(e) => update(b.key, i, { done: e.target.checked })}
                    data-testid={`bucket-${b.key}-item-${i}-done`}
                  />
                  <Textarea
                    className="bg-bg-base border-border-subtle text-fg-primary min-h-16 flex-1"
                    value={it.text}
                    onChange={(e) => update(b.key, i, { text: e.target.value })}
                    data-testid={`bucket-${b.key}-item-${i}-text`}
                    aria-label={`${b.label} item ${i + 1} text`}
                    placeholder="Describe this step in your own words"
                    maxLength={800}
                  />
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => remove(b.key, i)}
                    data-testid={`bucket-${b.key}-item-${i}-remove`}
                    aria-label={`Remove ${b.label} item ${i + 1}`}
                    className="text-fg-secondary hover:text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-8 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-4" data-testid="honest-plan-label">
          <p className="text-xs text-fg-primary">
            <strong>Participant progress and work-evidence summary.</strong>
            {" "}Not a certificate, diploma, license, employer verification, or a
            guarantee of employment or income.
          </p>
        </div>

        <div className="mt-6 flex justify-between">
          <Link to="/portfolio">
            <Button variant="ghost" data-testid="action-plan-back-portfolio" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Portfolio
            </Button>
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
